import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import { Model } from 'modules/core/lib/mobx-rest'

export const DUE_SOON_WARNING_PERIOD_HOURS = 7 * 24

export const STATUS = {
  Created: 'created',
  Acknowledged: 'acknowledged',
  Completed: 'completed',
  Discarded: 'discarded',
}

interface IComplaint {
  id: number
  identifier: string
  created_at: string
  acknowledged_at: string | null
  read_at: string | null
  completed_at: string | null
  discarded_at: string | null
  updated_at: string
  acknowledge_due_at: string
  due_at: string
  complainer_name: string | null
  complainer_email: string | null
  complainer_relationship: string
  status: string
  description: string
  blocked: boolean
  deleted_at: string | null
}

class Complaint extends Model<IComplaint> {
  static urlRoot() {
    return '/complaints/complaints'
  }

  constructor(attr: IComplaint) {
    super(attr)
    makeObservable(this, {
      id: computed,
      status: computed,
      description: computed,
      complainerName: computed,
      complainerEmail: computed,
      complainerRelationship: computed,
      createdAt: computed,
      acknowledgedAt: computed,
      completedAt: computed,
      discardedAt: computed,
      acknowledgeDueAt: computed,
      isDueSoon: computed,
      updatedAt: computed,
      identifier: computed,
      blocked: computed,
    })
  }

  get id(): number {
    return this.attributes.id
  }

  get identifier(): string {
    return this.attributes.identifier
  }

  get status(): string {
    return this.attributes.status
  }

  get description(): string {
    return this.attributes.description
  }

  get complainerName(): string | null {
    return this.attributes.complainer_name
  }

  get complainerEmail(): string | null {
    return this.attributes.complainer_email
  }

  get complainerRelationship(): string {
    return this.attributes.complainer_relationship
  }

  get createdAt(): Moment {
    return stringToDate(this.attributes.created_at, 'backend_datetime')
  }

  get updatedAt(): Moment | null {
    if (!this.attributes.updated_at) return null

    return stringToDate(this.attributes.updated_at, 'backend_datetime')
  }

  get acknowledgedAt(): Moment | null {
    if (!this.attributes.acknowledged_at) return null

    return stringToDate(this.attributes.acknowledged_at, 'backend_datetime')
  }

  get completedAt(): Moment | null {
    if (!this.attributes.completed_at) return null

    return stringToDate(this.attributes.completed_at, 'backend_datetime')
  }

  get discardedAt(): Moment | null {
    if (!this.attributes.discarded_at) return null

    return stringToDate(this.attributes.discarded_at, 'backend_datetime')
  }

  get acknowledgeDueAt(): Moment | null {
    if (!this.attributes.acknowledge_due_at) return null

    return stringToDate(this.attributes.acknowledge_due_at, 'backend_datetime')
  }

  get isAnonymous(): boolean {
    return !this.complainerName
  }

  get isDueSoon(): boolean {
    const endDate = stringToDate(this.attributes.due_at, 'backend_datetime')
    const todayDate = moment()
    // I'm using 'hours' instead of 'days' here because moment just takes into account whole days when
    // diffing days, so, if we are comparing today with a date that is 7 days and 12 hours from now
    // this would return true, if using 'days', because the diff would be 7 days, instead of 7 days
    // and 12 hours (7.5 days).
    const hourCountDiff = endDate.diff(todayDate, 'hours')

    return hourCountDiff >= 0 && hourCountDiff <= DUE_SOON_WARNING_PERIOD_HOURS
  }

  get blocked(): boolean {
    return this.attributes.blocked
  }
}

export default Complaint
